import { useState, useEffect, useContext } from 'react';

import { useCookie, useGeladaAccessTokenDecoder } from '@netfront/common-library';
import {
  DEFAULT_STORAGE_EXPIRY_OPTION,
  useDomain,
  useGetGeladaProject,
  useIdentitySiteUrls,
  useProtectedRoute,
} from '@netfront/gelada-identity-library';
import { AvatarBreadcrumbSectionTemplate, InformationBox } from '@netfront/ui-library';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { STYLE_LAYOUT_PAGE_CONSTANTS } from './StyleLayoutPage.constants';

import {
  getPlanUsagePercentage,
  getPlanUsageText,
  IProjectStyleSettings,
  LayoutPreviewer,
  PageTemplate,
  PlanUsage,
  StyleSettings,
  StyleSettingValue,
} from '../../../components';
import { CachingEntitiesContext, PlanContext } from '../../../contexts';
import { useLastProjectVisited, useToast, useUpdateProjectConfiguration } from '../../../hooks';
import { IProjectConfiguration } from '../../../interfaces';
import { BUTTON_CLASSES } from '../../LinkButton';

const { pageTitle } = STYLE_LAYOUT_PAGE_CONSTANTS;

const StyleLayoutPage = () => {
  const { createLastProjectVisitedCookie, getAccessTokenCookie, isSecureCookie } = useCookie();
  const { getDomain, isDomainReady } = useDomain();
  const { getDecodedJwt, getJwtUserId } = useGeladaAccessTokenDecoder();
  const { kanziProject } = useContext(PlanContext);
  const {
    isProjectConfigurationLoading,
    projectConfiguration: cachedProjectConfiguration,
    setProjectConfiguration: setCachedProjectConfiguration,
  } = useContext(CachingEntitiesContext);
  const { getBaseUrl } = useIdentitySiteUrls({
    environment: process.env.REACT_APP_ENVIRONMENT,
    port: process.env.REACT_APP_IDENTITY_SITE_LOCAL_PORT,
  });
  const { getLastProjectVisitedPath } = useLastProjectVisited();
  const { isAuthenticated } = useProtectedRoute({
    environment: process.env.REACT_APP_ENVIRONMENT,
    identitySitePort: process.env.REACT_APP_IDENTITY_SITE_LOCAL_PORT,
  });
  const {
    query: { organisationKey, projectId },
  } = useRouter();
  const { handleToastError, handleToastSuccess } = useToast();

  const [geladaProjectName, setGeladaProjectName] = useState<string>();
  const [hasRequiredPageData, setHasRequiredPageData] = useState<boolean>(false);
  const [identityUrl, setIdentityUrl] = useState<string>('');
  const [projectConfigurations, setProjectConfigurations] = useState<IProjectConfiguration>({} as IProjectConfiguration);
  const [projectConfigurationId, setProjectConfigurationId] = useState<number>();
  const [projectStyles, setProjectStyles] = useState<IProjectStyleSettings>({} as IProjectStyleSettings);
  const [userId, setUserId] = useState<number>();

  const accessToken = getAccessTokenCookie();

  const handleStyleSettingsChange = (field: string, value: StyleSettingValue) => {
    setProjectStyles(
      (prevState) =>
        ({
          ...prevState,
          [field]: value,
        } as IProjectStyleSettings),
    );

    if (field === 'isCollapse' && value) {
      setProjectStyles(
        (prevState) =>
          ({
            ...prevState,
            isDraggable: false,
          } as IProjectStyleSettings),
      );
    }
  };

  const handleStyleSettingsSave = () => {
    if (!projectConfigurationId) {
      return;
    }

    handleUpdateProjectConfiguration({
      id: projectConfigurationId,
      ...projectStyles,
    });
  };

  const { handleGetGeladaProject, isLoading: isGetProjectLoading = false } = useGetGeladaProject({
    fetchPolicy: 'cache-first',
    onCompleted: ({ geladaProject: { name } }) => {
      setGeladaProjectName(name);
    },
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const { handleUpdateProjectConfiguration, isLoading: isUpdateProjectConfigurationLoading = false } = useUpdateProjectConfiguration({
    onCompleted: () => {
      handleToastSuccess({
        message: 'Updated',
      });

      setCachedProjectConfiguration({
        ...projectConfigurations,
        ...projectStyles,
      } as IProjectConfiguration);
    },
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  useEffect(() => {
    if (!cachedProjectConfiguration) {
      return;
    }

    const { colour, cornerRadius, id, isCollapse, isSticky, offset, position, size, theme } = cachedProjectConfiguration;

    setProjectConfigurations(cachedProjectConfiguration);
    setProjectConfigurationId(id);

    setProjectStyles({
      colour,
      cornerRadius,
      isCollapse,
      isSticky,
      offset,
      position,
      size,
      theme,
    });

    setHasRequiredPageData(true);
  }, [cachedProjectConfiguration]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const decodedJwt = getDecodedJwt(accessToken);

    setUserId(getJwtUserId(decodedJwt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (!(isAuthenticated && isDomainReady && organisationKey && projectId && userId)) {
      return;
    }

    setIdentityUrl(getBaseUrl());
    createLastProjectVisitedCookie({
      optionalCookieAttributesInput: {
        domain: getDomain(),
        secure: isSecureCookie(process.env.REACT_APP_COOKIE_ATTRIBUTE_SECURE),
        storageExpiryOptions: DEFAULT_STORAGE_EXPIRY_OPTION,
      },
      value: getLastProjectVisitedPath(organisationKey, projectId, userId),
    });

    handleGetGeladaProject({
      projectId: String(projectId),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isDomainReady, organisationKey, projectId, userId]);

  const isLoading = isGetProjectLoading || isProjectConfigurationLoading || isUpdateProjectConfigurationLoading;

  return (
    <PageTemplate 
      activePage="style" 
      isLoading={isLoading} 
      pageTitle={pageTitle}
      topLevelChildren={
        <>
          {hasRequiredPageData && (
            <>
              {kanziProject && (
                <PlanUsage
                  plans={[
                    {
                      color: 'var(--g-color-red-400)',
                      label: 'Translate usage',
                      percentage: getPlanUsagePercentage(kanziProject.allowedTranslate, kanziProject.remainingTranslate),
                      usage: getPlanUsageText(kanziProject.allowedTranslate, kanziProject.remainingTranslate),
                    },
                    {
                      color: 'var(--g-color-orange-200)',
                      label: 'Speech usage',
                      percentage: getPlanUsagePercentage(kanziProject.allowedSpeech, kanziProject.remainingSpeech),
                      usage: getPlanUsageText(kanziProject.allowedSpeech, kanziProject.remainingSpeech),
                    },
                    {
                      color: 'var(--g-color-blue-200)',
                      label: 'PDF usage',
                      percentage: getPlanUsagePercentage(kanziProject.allowedPdf, kanziProject.remainingPdf),
                      usage: getPlanUsageText(kanziProject.allowedPdf, kanziProject.remainingPdf),
                    },
                  ]}
                />
              )}
            </>
          )}
        </>
      }
    >
      <>
        {hasRequiredPageData && (
          <>
            <div className="flex flex-col mb-8 lg:flex-row lg:items-center">
              {geladaProjectName ? (
                <AvatarBreadcrumbSectionTemplate
                  additionalClassNames="flex-1 mb-8 md:mb-0"
                  breadcrumbItems={[
                    {
                      key: '0',
                      content: (
                        <Link
                          className={`${BUTTON_CLASSES['subtle-link']} color-black`}
                          href={`${identityUrl}/dashboard`}
                        >
                          <span>Dashboard</span>
                        </Link>
                      ),
                    },
                    {
                      key: '1',
                      content: <span>{pageTitle}</span>,
                    },
                  ]}
                  title={geladaProjectName}
                />
              ) : null}
            </div>

            <InformationBox
              additionalClassNames="mb-8"
              iconId="id_info_icon"
              message="Configure your KANZI tool position, colour and theme to match your project style."
            />

            <div className="flex flex-1 flex-wrap -mx-4">
              <div className="w-full lg:w-1/2 px-4">
                <h3 className="h4 font-body mb-6">{pageTitle}</h3>
                <StyleSettings
                  projectConfiguration={projectConfigurations}
                  styles={projectStyles}
                  onChange={handleStyleSettingsChange}
                  onSave={handleStyleSettingsSave}
                />
              </div>

              <div className="w-full lg:w-1/2 px-4">
                <LayoutPreviewer colour={projectStyles.colour} position={projectStyles.position} />
              </div>
            </div>
          </>
        )}
      </>
    </PageTemplate>
  );
};

export { StyleLayoutPage };
